import Link from 'next/link';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteE } from 'src/constants/pages.constants';
import { useAuthorized } from 'src/features/common/auth/hooks/auth.hooks';
import { useUniverseMine } from 'src/features/common/universe/hooks/universe-mine.hooks';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { Button } from 'src/ui/common/atoms/button';
import { Typography } from 'src/ui/common/atoms/typography';
import { Visible } from 'src/ui/common/layouts/visible';
import {
    StyledButtonsWrapper,
    StyledCanvas,
    StyledContent,
    StyledDescription,
    StyledInfoBlock,
    StyledTopBanner,
    StyledWrapper,
} from './landing-top-banner.styles';

interface ILocale {
    title: string;
    text: string;
    createNftButton: string;
    exploreButton: string;
    createNftStore: string;
    learnMore: string;
    trendingCollectionsButton: string;
}

export const LandingTopBannerContainer = () => {
    const { t } = useTranslation(['pages']);
    const screenType = useScreenDetect();
    const isAuthorized = useAuthorized();
    const universeMine = useUniverseMine();

    const locale: ILocale = useMemo(
        () => ({
            ...t('home.widgets.topBanner', { ns: 'pages', returnObjects: true }),
        }),
        [t]
    );

    const titleFont = useMemo(() => {
        if (screenType.xs) {
            return 'md';
        }
        if (screenType.sm) {
            return 'lg';
        }
        return 'xl';
    }, [screenType]);

    return (
        <StyledTopBanner>
            <Visible view="mobile">
                <StyledCanvas>
                    <video
                        muted
                        autoPlay
                        src="https://storage.googleapis.com/spaceseven-public/static/Hero_mobile.mov"
                        playsInline
                        loop
                        controls={false}
                        controlsList="nodownload"
                    >
                        <source
                            src="https://storage.googleapis.com/spaceseven-public/tmp_uploads/Hero_mobile.webm"
                            type="video/vebm"
                        />
                        <source
                            src="https://storage.googleapis.com/spaceseven-public/static/Hero_mobile.mov"
                            type='video/mp4; codecs="hvc1"'
                        />
                        <track kind="captions" srcLang="en" label="english_captions" />
                    </video>
                </StyledCanvas>
            </Visible>

            <Visible view={['desktop', 'laptop']}>
                <StyledCanvas>
                    <video
                        muted
                        autoPlay
                        src="https://storage.googleapis.com/spaceseven-public/tmp_uploads/Hero_desktop.webm"
                        playsInline
                        loop
                        controls={false}
                        controlsList="nodownload"
                    >
                        <source
                            src="https://storage.googleapis.com/spaceseven-public/tmp_uploads/Hero_desktop.webm"
                            type="video/vebm"
                        />
                        <source
                            src="https://storage.googleapis.com/spaceseven-public/tmp_uploads/Hero_desktop.mp4"
                            type="video/mp4"
                        />
                        <track kind="captions" srcLang="en" label="english_captions" />
                    </video>
                </StyledCanvas>
            </Visible>

            <StyledWrapper>
                <StyledContent>
                    <StyledInfoBlock>
                        <div>
                            <Typography size={titleFont} variant="title" color="text">
                                {locale.title}
                            </Typography>
                            <StyledDescription
                                size={screenType.xs || screenType.sm ? 'xs' : 'sm'}
                                variant="text"
                                color="gs200"
                            >
                                {locale.text}
                            </StyledDescription>
                        </div>
                        <StyledButtonsWrapper justifyContent={screenType.xs ? 'space-between' : 'center'}>
                            <Button variant="white" size={screenType.xs || screenType.sm ? 'md' : 'lg'} stretch>
                                <Link href={RouteE.EXPLORE} prefetch={false}>
                                    {locale.exploreButton}
                                </Link>
                            </Button>

                            <Button variant="grey" size={screenType.xs || screenType.sm ? 'md' : 'lg'} stretch>
                                {' '}
                                <Link href={isAuthorized ? RouteE.COLLECTIBLE_CREATE : '/login'} prefetch={false}>
                                    {locale.createNftButton}
                                </Link>
                            </Button>
                        </StyledButtonsWrapper>
                    </StyledInfoBlock>
                </StyledContent>
            </StyledWrapper>
        </StyledTopBanner>
    );
};